import { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from "react";
import { CheckCircle, XCircle, Info, X } from "lucide-react";

const ToastContext = createContext(null);

const DEFAULT_DURATION = 4000;
const ERROR_DURATION = 6000;

const TOAST_STYLES = {
  success: { icon: CheckCircle, iconClass: "text-emerald-500", border: "border-emerald-200", bg: "bg-emerald-50" },
  error:   { icon: XCircle,     iconClass: "text-red-500",     border: "border-red-200",     bg: "bg-red-50"     },
  info:    { icon: Info,        iconClass: "text-sky-500",     border: "border-sky-200",     bg: "bg-sky-50"     },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});
  const counterRef = useRef(0);

  const dismiss = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (type, message, duration) => {
      counterRef.current += 1;
      const id = counterRef.current;
      setToasts((prev) => [...prev, { id, type, message }]);
      const ms = duration ?? (type === "error" ? ERROR_DURATION : DEFAULT_DURATION);
      timersRef.current[id] = setTimeout(() => dismiss(id), ms);
      return id;
    },
    [dismiss]
  );

  // Clear any pending timers if the provider unmounts
  useEffect(() => {
    const timers = timersRef.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  // Memoized so consumers can safely list `toast` in hook dependency arrays
  const api = useMemo(
    () => ({
      success: (message, duration) => show("success", message, duration),
      error:   (message, duration) => show("error", message, duration),
      info:    (message, duration) => show("info", message, duration),
      dismiss,
    }),
    [show, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="fixed z-[100] flex flex-col gap-2 top-4 right-4 left-4 sm:left-auto sm:w-[360px] pointer-events-none"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const s = TOAST_STYLES[t.type] || TOAST_STYLES.info;
          const Icon = s.icon;
          return (
            <div
              key={t.id}
              role={t.type === "error" ? "alert" : "status"}
              className={`pointer-events-auto flex items-start gap-3 px-4 py-3 bg-white border rounded-xl shadow-lg ${s.border}`}
            >
              <div className={`flex items-center justify-center flex-shrink-0 w-6 h-6 rounded-full ${s.bg}`}>
                <Icon size={15} className={s.iconClass} strokeWidth={2.2} />
              </div>
              <p className="flex-1 min-w-0 text-[13px] leading-snug text-neutral-800 break-words pt-0.5">
                {t.message}
              </p>
              <button
                onClick={() => dismiss(t.id)}
                className="flex-shrink-0 transition-colors text-neutral-400 hover:text-neutral-600"
                aria-label="Dismiss notification"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}

export default ToastContext;